import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { getCurrencySymbol } from 'utils/currencyUtils';
import withTranslate from '../../containers/widgets/withTranslate';
import {
    ARR_CURRENCY,
    DATA_BADGE_MAP,
    DATA_TEST_ID_AD_SPEND_CAP,
    DATA_TEST_ID_CAP,
    DATA_TEST_ID_CURRENCY,
    DATA_TEST_ID_END_DATE,
    DATA_TEST_ID_ORG_PLAN_DETAILS,
    DATA_TEST_ID_START_DATE,
    DATA_TEST_ID_TIMEZONE,
    PACING_TYPES,
} from '../consts';
import { calculateColorByContractPercent, contractPercent, getOrgPlanDetailsBadgeText } from '../utils';
import { getAdminModeEnabled, getIsPayingCustomer, getOrganizationTier } from '../../selectors/user';
import css from './OrgPlanDetails.css';
import { Button, Label } from '../../components/widgets';
import CircularProgressBar from '../../components/molecules/CircularProgressBar';
import PlanDetailsButton from './PlanDetailsButton';
import { CUSTOMER_TIERS } from '../../utils/consts';
import Card from './Card';

const OrgPlanDetails = ({
    type,
    timezone,
    currency,
    totalConversionsCap = 0,
    adSpendCap = 0,
    contractStartDate,
    contractEndDate,
    translate,
    planPrice = 0,
    pacingTextWrapper = () => '',
    upgradePlansRef = null,
}) => {
    const adminMode = useSelector(state => getAdminModeEnabled(state));
    const tier = useSelector(getOrganizationTier) || '';
    const isPaying = useSelector(getIsPayingCustomer);
    const percent = contractPercent(contractStartDate, contractEndDate);
    const planTimeColor = calculateColorByContractPercent(percent, contractEndDate);

    const renderContent = () => (
        <>
            {type === PACING_TYPES.CONVERSIONS ? (
                <div className={css.labelWrapper} data-testid={DATA_TEST_ID_CAP}>
                    <Label
                        className={css.labelTitle}
                        text={`${
                            totalConversionsCap === Infinity
                                ? translate('STATIC.PAGES.ACCOUNT_PLAN.UNLIMITED')
                                : totalConversionsCap.toLocaleString()
                        } ${translate(`STATIC.PAGES.ACCOUNT_PLAN.${isPaying ? 'PAID' : 'FREE'}_INSTALLS`)}`}
                    />
                    {isPaying ? (
                        <Label
                            type="p"
                            text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP')}
                            tooltip="STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PACING_CARD"
                        />
                    ) : (
                        <>
                            <Label type="p" text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP_FREE')} />
                            <Label
                                type="p"
                                text="STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP_FREE_INFO"
                                tooltip="STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PACING_CARD"
                            />
                        </>
                    )}
                </div>
            ) : (
                <div className={css.labelWrapper} data-testid={DATA_TEST_ID_AD_SPEND_CAP}>
                    <Label
                        className={css.labelTitle}
                        text={
                            adSpendCap === Infinity
                                ? 'STATIC.PAGES.ACCOUNT_PLAN.UNLIMITED'
                                : `${getCurrencySymbol(ARR_CURRENCY)}${adSpendCap.toLocaleString()} ${ARR_CURRENCY}`
                        }
                    />
                    <Label type="p" text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.AD_SPEND_CAP')} />
                </div>
            )}
            <div className={css.dateInfoWrapper}>
                <CircularProgressBar
                    progressPercentage={percent}
                    strokeColor={planTimeColor}
                    iconName="calendar-week-filled"
                />
                <div className={css.dateInfo}>
                    <div className={css.labelWrapper} data-testid={DATA_TEST_ID_END_DATE}>
                        <Label
                            className={classNames(css.labelTitle, css.endDate)}
                            text={contractEndDate}
                            style={{
                                '--end-date-color':
                                    planTimeColor === 'var(--attention)' ? 'var(--attention)' : 'var(--gray700)',
                            }}
                        />
                        <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.EXPIRATION_DATE" />
                    </div>
                    <div className={css.labelWrapper} data-testid={DATA_TEST_ID_START_DATE}>
                        <Label className={css.labelTitle} text={contractStartDate} />
                        <Label
                            type="p"
                            text="STATIC.PAGES.ACCOUNT_PLAN.START_DATE"
                            tooltip="STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PLAN_DETAILS_START_DATE"
                        />
                    </div>
                </div>
            </div>
            <div className={css.labelWrapper} data-testid={DATA_TEST_ID_CURRENCY}>
                <Label className={css.labelTitle} text={currency} />
                <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.CURRENCY" />
            </div>
            <div className={css.labelWrapper} data-testid={DATA_TEST_ID_TIMEZONE}>
                <Label className={css.labelTitle} text={timezone} />
                <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.TIMEZONE" />
            </div>
            {adminMode && (
                <div className={css.labelWrapper}>
                    <Label
                        className={css.labelTitle}
                        text={`${getCurrencySymbol(ARR_CURRENCY)} ${planPrice.toLocaleString()} ${ARR_CURRENCY}`}
                    />
                    <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.PLAN_PRICE" />
                </div>
            )}
        </>
    );

    return (
        <Card
            dataTestId={DATA_TEST_ID_ORG_PLAN_DETAILS}
            contentClassName={css.content}
            header={
                <>
                    <div className={css.headerMain}>
                        <div className={css.title}>
                            <Translate id="STATIC.PAGES.ACCOUNT_PLAN.PLAN_DETAILS" />
                        </div>
                        <div
                            className={classNames(css.tierBadge, css[tier.toLowerCase()])}
                            data-testid={DATA_BADGE_MAP[tier]}
                        >
                            <Translate id={getOrgPlanDetailsBadgeText(tier)} />
                        </div>
                    </div>
                    {tier === CUSTOMER_TIERS.FREE ? (
                        <Button
                            type="secondary"
                            text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.BUY_CONVERSIONS"
                            onClick={() => upgradePlansRef?.current?.scrollIntoView({ behavior: 'smooth' })}
                        />
                    ) : (
                        <PlanDetailsButton />
                    )}
                </>
            }
        >
            {renderContent()}
        </Card>
    );
};

OrgPlanDetails.propTypes = {
    type: PropTypes.oneOf(Object.values(PACING_TYPES)).isRequired,
    timezone: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    contractStartDate: PropTypes.string.isRequired,
    contractEndDate: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    planPrice: PropTypes.number,
    totalConversionsCap: PropTypes.number,
    adSpendCap: PropTypes.number,
    pacingTextWrapper: PropTypes.func,
    upgradePlansRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
};

export default withTranslate(OrgPlanDetails);
